// Mobile Performance Optimizer
// Handles mobile-specific performance issues and memory management

interface PerformanceConfig {
  enableThrottling: boolean;
  throttleInterval: number;
  enableMemoryCleanup: boolean;
  cleanupInterval: number;
  enableVirtualScrolling: boolean;
  maxRenderedItems: number;
}

class MobilePerformanceOptimizer {
  private config: PerformanceConfig = {
    enableThrottling: true,
    throttleInterval: 100, // 100ms throttling
    enableMemoryCleanup: true,
    cleanupInterval: 30000, // 30 seconds
    enableVirtualScrolling: true,
    maxRenderedItems: 50
  };

  private cleanupTimer: NodeJS.Timeout | null = null;
  private isMobile: boolean = false;

  constructor() {
    this.detectMobile();
    this.initializeOptimizations();
  }

  private detectMobile(): void {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   window.innerWidth <= 768;
  }

  private initializeOptimizations(): void {
    if (this.isMobile && this.config.enableMemoryCleanup) {
      this.startMemoryCleanup();
    }
  }

  /**
   * Throttle function calls for better mobile performance
   */
  throttle<T extends (...args: any[]) => any>(
    func: T,
    delay: number = this.config.throttleInterval
  ): (...args: Parameters<T>) => void {
    let timeoutId: NodeJS.Timeout | null = null;
    let lastExecTime = 0;

    return (...args: Parameters<T>) => {
      const currentTime = Date.now();

      if (currentTime - lastExecTime > delay) {
        func(...args);
        lastExecTime = currentTime;
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          func(...args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  /**
   * Debounce function calls to prevent excessive execution
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number = this.config.throttleInterval
  ): (...args: Parameters<T>) => void {
    let timeoutId: NodeJS.Timeout | null = null;

    return (...args: Parameters<T>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  /**
   * Optimize scroll events for mobile
   */
  optimizeScrollEvent(element: HTMLElement, callback: () => void): () => void {
    let ticking = false;

    const optimizedCallback = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          callback();
          ticking = false;
        });
        ticking = true;
      }
    };

    element.addEventListener('scroll', optimizedCallback, { passive: true });
    
    return () => {
      element.removeEventListener('scroll', optimizedCallback);
    };
  }

  /**
   * Start memory cleanup process
   */
  private startMemoryCleanup(): void {
    this.cleanupTimer = setInterval(() => {
      this.performMemoryCleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Perform memory cleanup
   */
  private performMemoryCleanup(): void {
    // Force garbage collection if available
    if (window.gc && typeof window.gc === 'function') {
      window.gc();
    }

    // Clear unused timers and intervals
    this.clearUnusedTimers();

    // Log memory usage in development
    if (process.env.NODE_ENV === 'development') {
      this.logMemoryUsage();
    }
  }

  /**
   * Clear unused timers and intervals
   */
  private clearUnusedTimers(): void {
    // This would need to be implemented with a timer registry
    // For now, we'll just log that cleanup is happening
    // console.log('Performing memory cleanup...');
  }

  /**
   * Log memory usage for debugging
   */
  private logMemoryUsage(): void {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      // console.log('Memory usage:', {
      //   used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + ' MB',
      //   total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + ' MB',
      //   limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
      // });
    }
  }

  /**
   * Optimize dropdown performance for mobile
   */
  optimizeDropdown(element: HTMLElement): void {
    if (!this.isMobile) return;

    // Reduce animation complexity on mobile
    element.style.transition = 'opacity 0.2s ease';
    
    // Use transform instead of changing layout properties
    element.style.transform = 'translateZ(0)'; // Enable hardware acceleration
  }

  /**
   * Optimize chart performance for mobile
   */
  optimizeChart(chartElement: HTMLElement): void {
    if (!this.isMobile) return;

    // Enable hardware acceleration
    chartElement.style.transform = 'translateZ(0)';
    chartElement.style.willChange = 'transform';
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Get performance configuration
   */
  getConfig(): PerformanceConfig {
    return { ...this.config };
  }

  /**
   * Update performance configuration
   */
  updateConfig(newConfig: Partial<PerformanceConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create singleton instance
export const mobilePerformanceOptimizer = new MobilePerformanceOptimizer();

// Export types
export type { PerformanceConfig };
